import * as React from "react";
import { EntityType } from "../../types/EntityType";
import IRepertoireComponentDataItem from "../../../redux/types/IRepertoireComponentDataItem";
import { useAddToPackageContext } from "./AddToPackageContext";
import { IYesNoPromptViewModalProps } from "../modalViews/yesNoPromptView/YesNoPromptView";
import { showYesNoPrompt, hideModal } from "../../../redux/reducers/ModalReducer";
import { IAddtoPackageResult } from "../../types/IAddtoPackageResult";
import { IRepertoireField } from "../../types/IRepertoireField";
import { CURRENT_USER_EMAIL_KEY, CURRENT_USER_NAME_KEY } from "../../../redux/services/AccountSessionStorage";

export interface IAddAllToPackageButtonProps {
    entityIds: number[];
    entityType: EntityType;
    addToPackages?: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult> | IAddtoPackageResult;
    removeFromPackages?: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult> | IAddtoPackageResult;
    resultPageData: IRepertoireComponentDataItem;
    showYesNoPrompt: typeof showYesNoPrompt;
    hideModal: typeof hideModal;
    searchResultsTableData: IRepertoireField[];
    disabled?: boolean;
}

const AddAllToPackageButton: React.FC<IAddAllToPackageButtonProps> = ({
    entityIds,
    entityType,
    addToPackages,
    removeFromPackages,
    resultPageData,
    showYesNoPrompt,
    hideModal,
    searchResultsTableData,
    disabled = false,
}) => {
    const ctx = useAddToPackageContext();

    const addedIds = ctx?.addedIds ?? [];
    const addIds = ctx?.addIds ?? (() => undefined);
    const removeIds = ctx?.removeIds ?? (() => undefined);

    const getText = React.useCallback(
        (key: string, fallback: string): string =>
            searchResultsTableData.find(
                field => field.name?.toString() === key
            )?.data || fallback,
        [searchResultsTableData]
    );

    const allDisplayedAdded = entityIds.length > 0 && entityIds.every(id => addedIds.includes(id));
    const canRemoveAll = allDisplayedAdded && Boolean(removeFromPackages);

    const [messageText, setMessageText] = React.useState("");
    const [messageType, setMessageType] = React.useState<"success" | "error" | undefined>();
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [isDone, setIsDone] = React.useState(false);

    const messageClass =
        messageType === "error"
            ? "addtopackageerrorMessage"
            : "addtopackagesuccessMessage";

    const runPackageOperation = React.useCallback(async () => {
        if (entityIds.length === 0) {
            return;
        }

        const userName = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY) || sessionStorage.getItem(CURRENT_USER_NAME_KEY);

        if (!userName) {
            return;
        }

        const operation = canRemoveAll ? removeFromPackages : addToPackages;

        if (!operation) {
            return;
        }

        setIsProcessing(true);

        try {
            const response = await Promise.resolve(operation(userName, entityType, entityIds));

            const messageKey = response?.messages?.[0]?.messageKey;

            const translatedMessage =
                resultPageData?.fields?.find(
                    field => field.name?.toString() === messageKey
                )?.data;

            if (response?.success) {
                if (canRemoveAll) {
                    removeIds(entityIds);
                } else {
                    const persistedIds = response.MyPackages?.packageItems ?? entityIds;
                    addIds(persistedIds);
                }

                setMessageType("success");
                setIsDone(true);
            } else {
                setMessageType("error");
                setIsDone(false);
            }

            setMessageText(
                translatedMessage || messageKey || ""
            );

            window.setTimeout(() => setMessageText(""), 5000);
        } catch {
            setMessageType("error");
            setIsDone(false);

            setMessageText(
                getText(
                    "addtoPackageValidationFailedMessage",
                    "Could not update package."
                )
            );
        } finally {
            setIsProcessing(false);
        }
    }, [entityIds, entityType, canRemoveAll, addToPackages, removeFromPackages, resultPageData, addIds, removeIds, getText]);

    const handleClick = React.useCallback(() => {
        const modalProps: IYesNoPromptViewModalProps = {
            yesCallback: async () => {
                await runPackageOperation();
                hideModal();
            },
            noCallback: () => {
                hideModal();
            },
        };

        const headerKey = canRemoveAll ? "removeAllFromPackageHeader" : "addAllToPackageHeader";
        const promptKey = canRemoveAll ? "removeAllFromPackagePrompt" : "addAllToPackagePrompt";

        const confirmationHeader = getText(headerKey, canRemoveAll ? "Remove items from package" : "Add items to package");
        const confirmationPrompt = getText(promptKey, canRemoveAll
            ? "Are you sure you want to remove all displayed items from the package?"
            : "Are you sure you want to add all displayed items to the package?"
        );

        showYesNoPrompt(confirmationHeader, modalProps, undefined, confirmationPrompt);
    }, [runPackageOperation, showYesNoPrompt, hideModal, canRemoveAll, getText]);

    const isDisabled = disabled || isProcessing || entityIds.length === 0;

    const selectAllText = getText("selectAllForPackage", "Select All");
    const addingText = getText("processingSelectAllForPackage", "Adding...");
    const unselectAllText = getText("unselectAllForPackage", "Unselect All");
    const removingText = getText("processingUnselectAllForPackage", "Removing...");
    const buttonText = isProcessing
        ? canRemoveAll
            ? removingText
            : addingText
        : canRemoveAll
            ? unselectAllText
            : selectAllText;

    return (
        <div className="addAllToPackageButtonWrapper">
            <button
                type="button"
                className={["add-all-to-package-button", isProcessing && "is-loading", isDone && "is-success", messageType === "error" && "is-error",].filter(Boolean).join(" ")}
                onClick={handleClick}
                disabled={isDisabled}
            >
                {buttonText}
            </button>

            {messageText && (
                <div className={`messageBox ${messageClass}`}>{messageText}</div>
            )}
        </div>
    );
};

export default AddAllToPackageButton;
